import { thresholdAngle, thresholdValue } from './const';
import { IPoint2d } from './IPoint2d';

export class Line2d {
  // 私有属性存储直线的不同表示形式
  private _pointForm?: { p1: IPoint2d; p2: IPoint2d };
  private _slopeInterceptForm?: { k: number; b: number };
  private _generalForm?: { A: number; B: number; C: number };
  private _parametricForm?: { point: IPoint2d; direction: IPoint2d };

  /**
   * 构造方法 - 支持多种直线定义方式
   */
  constructor(params: {
    // 两点式
    points?: { p1: IPoint2d; p2: IPoint2d };
    // 斜截式
    slopeIntercept?: { k: number; b: number };
    // 一般式
    generalForm?: { A: number; B: number; C: number };
    // 参数式
    parametric?: { point: IPoint2d; direction: IPoint2d };
  }) {
    if (params.points) {
      this._pointForm = params.points;
    } else if (params.slopeIntercept) {
      this._slopeInterceptForm = params.slopeIntercept;
    } else if (params.generalForm) {
      this._generalForm = params.generalForm;
    } else if (params.parametric) {
      this._parametricForm = params.parametric;
    } else {
      throw new Error('必须提供至少一种直线定义方式');
    }
  }

  // 获取两点式表示
  private get pointForm(): { p1: IPoint2d; p2: IPoint2d } {
    if (!this._pointForm) {
      if (this._slopeInterceptForm) {
        // 从斜截式转换为两点式
        const { k, b } = this._slopeInterceptForm;
        const p1 = { x: 0, y: b };
        const p2 = { x: 1, y: k + b };
        this._pointForm = { p1, p2 };
      } else if (this._generalForm) {
        // 从一般式转换为两点式
        const { A, B, C } = this._generalForm;
        if (Math.abs(B) > thresholdValue) {
          const p1 = { x: 0, y: -C / B };
          const p2 = { x: 1, y: (-A - C) / B };
          this._pointForm = { p1, p2 };
        } else {
          // 垂直线 (x = -C/A)
          const x = -C / A;
          const p1 = { x, y: 0 };
          const p2 = { x, y: 1 };
          this._pointForm = { p1, p2 };
        }
      } else if (this._parametricForm) {
        // 从参数式转换为两点式
        const { point, direction } = this._parametricForm;
        const p1 = point;
        const p2 = {
          x: point.x + direction.x,
          y: point.y + direction.y
        };
        this._pointForm = { p1, p2 };
      } else {
        throw new Error('直线未持有任何定义形式，无法转换为两点式');
      }
    }
    return this._pointForm;
  }

  // 获取斜截式表示
  private get slopeInterceptForm(): { k: number; b: number } | null {
    if (!this._slopeInterceptForm) {
      const general = this.generalForm;
      if (Math.abs(general.B) < thresholdValue) {
        // 垂直线无法用斜截式表示
        return null;
      }
      this._slopeInterceptForm = {
        k: -general.A / general.B,
        b: -general.C / general.B
      };
    }
    return this._slopeInterceptForm;
  }

  // 获取一般式表示
  get generalForm(): { A: number; B: number; C: number } {
    if (!this._generalForm) {
      if (this._pointForm) {
        // 从两点式转换为一般式
        const { p1, p2 } = this._pointForm;
        const A = p2.y - p1.y;
        const B = p1.x - p2.x;
        // C 用点代入求，等价于 p2.x * p1.y - p1.x * p2.y，但后者在直线远离原点时是两个大数相减，会丢掉约 1e-10 的精度
        const C = -(A * p1.x + B * p1.y);
        this._generalForm = { A, B, C };
      } else if (this._slopeInterceptForm) {
        // 从斜截式转换为一般式
        const { k, b } = this._slopeInterceptForm;
        this._generalForm = { A: k, B: -1, C: b };
      } else if (this._parametricForm) {
        // 从参数式转换为一般式
        const { point, direction } = this._parametricForm;
        const A = direction.y;
        const B = -direction.x;
        const C = direction.x * point.y - direction.y * point.x;
        this._generalForm = { A, B, C };
      } else {
        throw new Error('直线未持有任何定义形式，无法转换为一般式');
      }
    }
    return this._generalForm;
  }

  // 获取参数式表示
  get parametricForm(): { point: IPoint2d; direction: IPoint2d } {
    if (!this._parametricForm) {
      const { p1, p2 } = this.pointForm;
      this._parametricForm = {
        point: p1,
        direction: { x: p2.x - p1.x, y: p2.y - p1.y }
      };
    }
    return this._parametricForm;
  }

  /**
   * 判断两条直线是否平行
   */
  isParallelTo(line: Line2d, tolerance: number = thresholdValue): boolean {
    const thisPoints = this.pointForm;
    const otherPoints = line.pointForm;
    const thisVector = {
      x: thisPoints.p2.x - thisPoints.p1.x,
      y: thisPoints.p2.y - thisPoints.p1.y
    };
    const otherVector = {
      x: otherPoints.p2.x - otherPoints.p1.x,
      y: otherPoints.p2.y - otherPoints.p1.y
    };

    // 计算叉积
    const cross = thisVector.x * otherVector.y - thisVector.y * otherVector.x;
    const magnitude1 = Math.sqrt(thisVector.x * thisVector.x + thisVector.y * thisVector.y);
    const magnitude2 = Math.sqrt(otherVector.x * otherVector.x + otherVector.y * otherVector.y);
    const sinValue = cross / (magnitude1 * magnitude2);

    // sin在pi/2附近斜率接近1，直接使用sin值替换角度值进行判断
    return Math.abs(sinValue) < tolerance;
  }

  /**
   * 判断两条直线是否重合
   */
  isCoincidentWith(other: Line2d, tolerance: number = thresholdValue): boolean {
    if (!this.isParallelTo(other, tolerance)) return false;

    const thisGeneral = this.generalForm;
    const otherGeneral = other.generalForm;

    // 检查比例关系
    const ratioA =
      Math.abs(thisGeneral.A) > tolerance ? otherGeneral.A / thisGeneral.A : otherGeneral.B / thisGeneral.B;

    return (
      Math.abs(thisGeneral.B * ratioA - otherGeneral.B) < tolerance &&
      Math.abs(thisGeneral.C * ratioA - otherGeneral.C) < tolerance
    );
  }

  /**
   * 计算两条直线的交点
   */
  intersection(other: Line2d, tolerance: number = thresholdValue): IPoint2d | null {
    if (this.isParallelTo(other, tolerance)) {
      // return this.isCoincidentWith(other, tolerance)
      //     ? new Point2d(Infinity, Infinity)
      //     : null;
      return null;
    }

    const thisGeneral = this.generalForm;
    const otherGeneral = other.generalForm;

    // 计算分母
    const denominator = thisGeneral.A * otherGeneral.B - otherGeneral.A * thisGeneral.B;

    // 计算交点坐标
    const x = (thisGeneral.B * otherGeneral.C - otherGeneral.B * thisGeneral.C) / denominator;
    const y = (otherGeneral.A * thisGeneral.C - thisGeneral.A * otherGeneral.C) / denominator;

    return { x, y };
  }

  /**
   * 判断点是否在直线上
   */
  pointOnLine(point: IPoint2d, tolerance: number = thresholdValue): boolean {
    const { A, B, C } = this.generalForm;
    const distance = Math.abs(A * point.x + B * point.y + C) / Math.sqrt(A * A + B * B);
    return distance < tolerance;
  }

  /**
   * 获取直线的斜率（垂直线返回Infinity）
   */
  getSlope(): number {
    const form = this.slopeInterceptForm;
    return form ? form.k : Infinity;
  }

  /**
   * 获取直线的y轴截距（垂直线返回null）
   */
  getYIntercept(): number | null {
    const form = this.slopeInterceptForm;
    return form ? form.b : null;
  }

  /**
   * 获取直线上的两点表示
   */
  getPoints(): [IPoint2d, IPoint2d] {
    const { p1, p2 } = this.pointForm;
    return [p1, p2];
  }

  toString(): string {
    const { A, B, C } = this.generalForm;
    return `${A.toFixed(2)}x + ${B.toFixed(2)}y + ${C.toFixed(2)} = 0`;
  }

  /**
   * 计算点到直线的距离
   * @param A 直线方程一般式中的 A
   * @param B 直线方程一般式中的 B
   * @param C 直线方程一般式中的 C
   * @param x0 点的 x 坐标
   * @param y0 点的 y 坐标
   * @returns 点到直线的距离
   */
  pointToLineDistance(point: IPoint2d): number {
    const { A, B, C } = this.generalForm;
    const numerator = Math.abs(A * point.x + B * point.y + C);
    const denominator = Math.sqrt(A * A + B * B);
    return numerator / denominator;
  }

  // 使用直线上的两点表示直线，点在直线上的投影
  pointProjectOnLine(point: IPoint2d): IPoint2d {
    const linePoint1 = this.pointForm.p1;
    const linePoint2 = this.pointForm.p2;
    // 计算直线方向向量
    const vx = linePoint2.x - linePoint1.x;
    const vy = linePoint2.y - linePoint1.y;

    // 计算向量AP
    const apx = point.x - linePoint1.x;
    const apy = point.y - linePoint1.y;

    // 计算投影参数t
    const t = (apx * vx + apy * vy) / (vx * vx + vy * vy);

    // 计算投影点坐标
    return { x: linePoint1.x + t * vx, y: linePoint1.y + t * vy };
  }

  // 使用点和方向向量表示直线，点在直线上的投影
  pointProjectionOnLineWithVector(point: IPoint2d, linePoint: IPoint2d, direction: IPoint2d): IPoint2d {
    // 计算向量AP
    const apx = point.x - linePoint.x;
    const apy = point.y - linePoint.y;

    // 计算投影参数t
    const t = (apx * direction.x + apy * direction.y) / (direction.x * direction.x + direction.y * direction.y);

    // 计算投影点坐标
    return { x: linePoint.x + t * direction.x, y: linePoint.y + t * direction.y };
  }

  isRightAngle(line: Line2d, tolerance: number = thresholdAngle): boolean {
    const thisPoints = this.pointForm;
    const otherPoints = line.pointForm;
    const thisVector = {
      x: thisPoints.p2.x - thisPoints.p1.x,
      y: thisPoints.p2.y - thisPoints.p1.y
    };
    const otherVector = {
      x: otherPoints.p2.x - otherPoints.p1.x,
      y: otherPoints.p2.y - otherPoints.p1.y
    };

    // 计算点积
    const dot = thisVector.x * otherVector.x + thisVector.y * otherVector.y;
    const magnitude1 = Math.sqrt(thisVector.x * thisVector.x + thisVector.y * thisVector.y);
    const magnitude2 = Math.sqrt(otherVector.x * otherVector.x + otherVector.y * otherVector.y);
    const cosValue = dot / (magnitude1 * magnitude2);

    // cos在pi/2附近斜率接近1，直接使用cos值替换角度值进行判断
    return Math.abs(cosValue) < tolerance;
  }

  isHorizontal() {
    const { p1, p2 } = this.pointForm;
    const isHorizontal = Math.abs(p2.y - p1.y) < thresholdValue;

    return isHorizontal;
  }

  isVertical() {
    const { p1, p2 } = this.pointForm;
    const isVertical = Math.abs(p2.x - p1.x) < thresholdValue;

    return isVertical;
  }

  /**
   * 计算两条直线之间的夹角（通过一般式方程）
   * 直线方程：Ax + By + C = 0
   */
  angleBetweenLinesByEquation(line: Line2d): number {
    // 计算法向量的点积
    const { A: A1, B: B1 } = this.generalForm;
    const { A: A2, B: B2 } = line.generalForm;
    const dotProduct = A1 * A2 + B1 * B2;

    // 计算法向量的模长
    const magnitude1 = Math.sqrt(A1 * A1 + B1 * B1);
    const magnitude2 = Math.sqrt(A2 * A2 + B2 * B2);

    // 避免除零错误
    if (magnitude1 === 0 || magnitude2 === 0) {
      throw new Error('法向量长度不能为零');
    }

    // 计算余弦值
    const cosTheta = dotProduct / (magnitude1 * magnitude2);
    const clampedCosTheta = Math.max(-1, Math.min(1, cosTheta));

    // 计算夹角（弧度）
    const angleRad = Math.acos(clampedCosTheta);

    // 转换为角度并返回锐角
    const angleDeg = (angleRad * 180) / Math.PI;
    return Math.min(angleDeg, 180 - angleDeg);
  }
}
